// i18next-extract-mark-ns-start blog

import React from 'react';
import styled from 'styled-components';
import RocketSvg from 'images/blog-rocket.svg';
import {Link, Trans} from 'gatsby-plugin-react-i18next';
import TrendgraphSvg from 'images/trendgraph.svg';
import ArrowRightSvg from 'images/arrow-right.svg';
import HeartballoonSvg from 'images/heartballoon.svg';
import {RichText} from 'prismic-reactjs';
import {BlogPost} from '../../../types/BlogTypes';
import {SignUpButton} from '../SignUpButton';
import {ScreenSizes} from '../../../types/responsive';
import {ButtonLink} from 'components/Button';

export const blogSidebarContainerWidth = '368px';
export const blogSidebarContainerWidthLg = '308px';

const Container = styled.div`
  display: flex;
  width: ${blogSidebarContainerWidth};
  background: #ffffff;
  box-shadow: 0 42px 80px rgba(0, 0, 0, 0.04), 0 15.3307px 29.2013px rgba(0, 0, 0, 0.0275993),
    0 7.44277px 14.1767px rgba(0, 0, 0, 0.0222516), 0 3.64858px 6.94968px rgba(0, 0, 0, 0.0177484),
    0 1.44265px 2.74791px rgba(0, 0, 0, 0.0124007);
  border-radius: 12px;
  flex-direction: column;
  height: fit-content;
  align-items: center;
  padding: 40px 32px;

  @media (max-width: ${ScreenSizes.lg}) {
    padding: 24px;
    margin-right: 20px;
    width: ${blogSidebarContainerWidthLg};
  }
  @media (max-width: ${ScreenSizes.md}) {
    display: none;
  }
`;

const SidebarInnerContainer = styled.div`
  background: #7984d9;
  border-radius: 8px;
  color: #ffffff;
  position: relative;
  padding: 40px 32px 32px 24px;
  width: 320px;
  transition: all 0.3s ease-in-out;
  height: auto;

  @media (max-width: ${ScreenSizes.lg}) {
    padding: 20px;
    width: 260px;
  }
`;

const SidebarInnerContainerEcommerce = styled.div`
  background: rgb(0, 233, 213);
  background: linear-gradient(45deg, rgba(0, 233, 213, 1) 0%, rgba(170, 180, 233, 1) 100%);
  border-radius: 8px;
  color: #ffffff;
  position: relative;
  padding: 40px 32px 32px 24px;
  width: 320px;
  transition: all 0.3s ease-in-out;
  height: auto;
  margin-top: 20px;

  @media (max-width: ${ScreenSizes.lg}) {
    padding: 20px;
    width: 260px;
  }
`;

const StickyContainer = styled.div`
  color: #ffffff;
  &.sticky {
    position: fixed;
    top: 90px;
    opacity: 0;
    z-index: 0;
    pointer-events: none;

    &.absolute {
      position: absolute;
      top: unset;
      bottom: 45px;
    }
  }

  &.visible {
    opacity: 1;
    pointer-events: all;
  }

  > * {
    color: inherit;
  }

  > a {
    width: 100%;
    min-width: unset;
  }
`;

const Rocket = styled.img`
  position: absolute;
  right: -4px;
  top: 0;
  transform: translate(20%, -7%);
  @media (max-width: ${ScreenSizes.lg}) {
    transform: translate(25%, -20%) scale(0.8);
  }
`;

const SidebarTitle = styled.h2`
  font-style: normal;
  font-weight: 400;
  font-size: 25px;
  line-height: 30px;
  margin: 0 0 1rem;
  @media (max-width: ${ScreenSizes.lg}) {
    font-size: 22px;
  }
`;

const SidebarBody = styled.p`
  font-style: normal;
  font-size: 13px;
  line-height: 19px;
  margin: 0 0 1.5rem;
`;

const SidebarCaption = styled.p`
  font-style: normal;
  font-size: 10px;
  line-height: 12px;
  margin: 1.5rem 0 0;
`;

const SidebarSection = styled.div`
  margin: 42px 24px 0;
  z-index: 1;

  > img {
    margin: 0;
  }
`;

const Image = styled.img``;

const H3 = styled.h3`
  margin: 1.25rem 0;
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 24px;
`;

const SidebarList = styled.ul`
  color: #475157;
  margin: 0;

  > li {
    color: inherit;
  }

  > li:not(:last-child) {
    padding: 0 0 1rem;
    border-bottom: 1px solid #e3e7e8;
  }
`;

const SidebarElement = styled.li`
  > a {
    color: inherit;
  }

  list-style: none;

  &:hover {
    color: #30373b;
  }
`;

const SidebarLiBody = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.05em;
  @media (max-width: ${ScreenSizes.lg}) {
    font-size: 14px;
    line-height: 22px;
  }
`;

const SidebarLink = styled(Link)`
  display: flex;
  gap: 0.5rem;

  > * {
    color: inherit;
  }
`;

type Props = {
  popularPosts: BlogPost[];
  mustReadPosts: BlogPost[];
};

const Cta: React.FC<{sticky?: boolean}> = ({sticky}) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isAbsolute, setIsAbsolute] = React.useState(false);

  const handleScroll = () => {
    if (window.scrollY > 1950) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
    const footer = document.getElementById('footer') as HTMLElement;
    //443 is height of cta container (343) + 100px of padding from footer section
    if (window.scrollY > footer.offsetTop - 443) {
      setIsAbsolute(true);
    } else {
      setIsAbsolute(false);
    }
  };

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <StickyContainer
      className={`${sticky ? 'sticky' : ''} ${isVisible ? 'visible' : ''} ${
        isAbsolute ? 'absolute' : ''
      }`}>
      <SidebarInnerContainer>
        <Rocket src={RocketSvg} alt="Rocket" />
        <SidebarTitle>
          <Trans>Boost customer satisfaction and sales by accepting more payment methods.</Trans>
        </SidebarTitle>
        <SidebarBody>
          <Trans>Join MONEI with no commitment to test integrations and payments.</Trans>
        </SidebarBody>
        <SignUpButton variant="dark" style={{width: '100%'}}>
          <Trans>Open an Account</Trans>
        </SignUpButton>
        <SidebarCaption>
          <Trans>No commitment. Unsubscribe anytime.</Trans>
        </SidebarCaption>
      </SidebarInnerContainer>
      <SidebarInnerContainerEcommerce>
        <SidebarTitle>
          <Trans>Increase your ecommerce sales by up to +40%</Trans>
        </SidebarTitle>
        <SidebarBody>
          <Trans>Download the step-by-step guide to increase turnover this 2024.</Trans>
        </SidebarBody>
        <ButtonLink
          href="https://monei.com/es/ecommerce-billing/?utm_source=blog&utm_medium=sidebar&utm_id=guide"
          variant="darkLight"
          style={{width: '100%'}}>
          <Trans>Download now</Trans>
        </ButtonLink>
      </SidebarInnerContainerEcommerce>
    </StickyContainer>
  );
};

const BlogSidebar: React.FC<Props> = ({popularPosts, mustReadPosts}) => (
  <Container>
    <Cta />
    <SidebarSection>
      <Image src={TrendgraphSvg} alt="Trend graph" />
      <H3>
        <Trans>Popular</Trans>
      </H3>
      <SidebarList>
        {popularPosts.map((post, index) => (
          <SidebarElement key={index}>
            <SidebarLink to={post.slug as string}>
              <SidebarLiBody>{RichText.asText(post.title)}</SidebarLiBody>
              <Image src={ArrowRightSvg} alt="Arrow right" />
            </SidebarLink>
          </SidebarElement>
        ))}
      </SidebarList>
    </SidebarSection>
    <SidebarSection>
      <Image src={HeartballoonSvg} alt="Heart balloon" />
      <H3>
        <Trans>Must Read</Trans>
      </H3>
      <SidebarList>
        {mustReadPosts.map((post, index) => (
          <SidebarElement key={index}>
            <SidebarLink to={post.slug as string}>
              <SidebarLiBody>{RichText.asText(post.title)}</SidebarLiBody>
              <Image src={ArrowRightSvg} alt="Arrow right" />
            </SidebarLink>
          </SidebarElement>
        ))}
      </SidebarList>
    </SidebarSection>
    <Cta sticky />
  </Container>
);

export default BlogSidebar;
